import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { closed } from "../../components/muidrawer/muiDrawerSlice";
import { checkAcessRights, formatIndianNumber } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  PRINT_ACCESS,
} from "../../_contstants/common";
import {
  clearCurrentPage,
  setCurrentPage,
  setPageLimit,
} from "../pagination/paginationSlice";

import { useNavigate } from "react-router-dom";
import _debounce from "lodash/debounce";
import { TextBox } from "@progress/kendo-react-inputs";
import { filterClearIcon } from "@progress/kendo-svg-icons";
import {
  clearFilterCustomExpenseApproved,
  setFilterCustomExpenseApproved,
} from "./expenseApprovedSlice";
import {
  getAllSRExpensesForVerify,
  getExpenseDetailsByID,
} from "./services/expenseApproved.services";
import { FiEdit } from "react-icons/fi";
import { FaEye } from "react-icons/fa";
import AppDialog from "../../components/dialog/Dialog";
import { RxCross2 } from "react-icons/rx";
import { clearExpenseDetail } from "../expensedetails/expenseDetailsSlice";
import moment from "moment";
import { MdPrint } from "react-icons/md";
import { ComboBox, ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";

const ExpenseApproved: React.FC = () => {
  const dispatch = useAppDispatch();

  const isEditRef = React.useRef(false);
  useEffect(() => {
    return () => {
      if (!isEditRef.current) {
        dispatch(closeDialog());
        dispatch(closed());
        dispatch(clearCurrentPage());
        dispatch(clearFilterCustomExpenseApproved());
      }
    };
  }, []);

  return (
    <>
      <ViewExpenseDetailsDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Expense Details List
          </Typography.h4>
        </GridLayoutItem>
        <GridLayoutItem>
          <SRExpensesGridComponent isEditRef={isEditRef} />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const SRExpensesGridComponent: React.FC<{
  isEditRef: React.MutableRefObject<boolean>;
}> = ({ isEditRef }) => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);
  const navigate = useNavigate();
  const FilterCustomExpenseApproved: any = useAppSelector(
    (state) => state.expenseApproved.FilterCustomExpenseApproved
  );
  const ExpenseApprovedList = useAppSelector(
    (state) => state.expenseApproved.ExpenseApprovedList
  );
  const loading = useAppSelector((state) => state.expenseApproved.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const [expandedRow, setExpandedRow] = useState(null);
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  useEffect(() => {
    const fetchItemList = async () => {
      const payload = {
        ...FilterCustomExpenseApproved,
        verification_status:FilterCustomExpenseApproved?.verification_status?.value,
        limit: +pageLimit,
        skip: +currentPage * +pageLimit,
      };
      await dispatch(getAllSRExpensesForVerify(payload));
    };
    fetchItemList();
  }, [currentPage, pageLimit]);

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (expense_details_guid: any) => {
      navigate("/srexpense/verify", {
        state: { expense_details_guid: expense_details_guid },
      });
      isEditRef.current = true;
    };
    const handleExpenseDetailsView = (ID: any) => {
      dispatch(getExpenseDetailsByID(ID)).then((res: any) => {
        if (res.meta?.requestStatus === "fulfilled") {
          dispatch(openDialog("viewSRExpenseDetails"));
        }
      });
    };

    const handlePrint = (expense_details_guid: any) => {
      window.open(`/expensedetailsprint/${expense_details_guid}`);
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Verify"
            onClick={() => {
              handleEdit(props.dataItem?.expense_details_guid);
            }}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}

        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="View"
          onClick={() =>
            handleExpenseDetailsView(props.dataItem?.expense_details_guid)
          }
        >
          <FaEye className="absolute-position" style={{ fontSize: "20px" }} />
        </IconButton>
        {isPrintAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Print"
            onClick={() => handlePrint(props.dataItem?.expense_details_guid)}
          >
            <MdPrint
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const truncateRemarks = (remarks: string) => {
    if (!remarks) return "";
    const maxLength = 100;
    if (remarks.length <= maxLength) return remarks;
    return remarks.substring(0, maxLength) + "...";
  };

  // const handleExpenseDetailsView = (ID: any) => {
  //   dispatch(openDialog("viewSRExpenseDetails"));
  //   dispatch(getSRExpenseByID(ID)).then((res: any) => {
  //     if (res.meta?.requestStatus === "fulfilled") {
  //       dispatch(openDialog("viewSRExpenseDetails"));
  //     }
  //   });
  // };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 10 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  //   if (loading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 152px)" }}
        className="responsive-table"
        filterable={true}
        filter={filter}
        // data={
        //   filter ? filterBy(ExpenseApprovedList, filter) : ExpenseApprovedList
        // }
        data={(filter
          ? filterBy(ExpenseApprovedList, filter)
          : ExpenseApprovedList
        ).map((item: any) => ({
          ...item,
          from_city: item?.from_city_name,
          to_city: item?.to_city_name,
        }))}
        onFilterChange={handleFilterChange}
        total={ExpenseApprovedList[0]?.itemcount || ExpenseApprovedList?.length}
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="verification_status"
          title="Verified Status"
          // filterable={false}
          filterCell={CommonCheckBoxFilterCell}
          cell={(props: any) => (
            <td className="text-center">
              <div>
                {props.dataItem?.verification_status ? (
                  <span
                    style={{
                      backgroundColor: "#D1FADF",
                      color: "#065032",
                      padding: "3px 10px",
                      textTransform: "capitalize",
                      borderRadius: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    Verified
                  </span>
                ) : (
                  <span
                    style={{
                      backgroundColor: "rgb(254, 240, 199)",
                      color: "rgb(128, 55, 23)",
                      padding: "3px 10px",
                      textTransform: "capitalize",
                      borderRadius: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    Pending
                  </span>
                )}
              </div>
            </td>
          )}
          width={150}
        />
        <Column
          width={200}
          field="sr_name"
          filterCell={CommonFilterCell}
          title="Sales Representative"
          cell={(props: any) => (
            <td>
              <div
                className={isEditAccess ? "hoverableName" : "normalName"}
                onClick={() =>
                  isEditAccess &&
                  navigate("/srexpense/verify", {
                    state: {
                      expense_details_guid:
                        props.dataItem?.expense_details_guid,
                    },
                  })
                }
              >
                {props.dataItem?.sr_name}
              </div>
            </td>
          )}
        />

        <Column
          width={130}
          field="expense_date"
          title="Expense Date"
          filterCell={CommonFilterCell}
        />

        <Column
          width={200}
          field="from_city"
          filterCell={CommonFilterCell}
          title="From City"
        />
        <Column
          width={200}
          field="to_city"
          filterCell={CommonFilterCell}
          title="To City"
        />
        <Column
          field="net_amount"
          title="Net Amount"
          filterable={false}
          cell={(props: any) => (
            <td className="text-end">
              {formatIndianNumber(props.dataItem?.net_amount || 0)}
            </td>
          )}
          width={180}
        />

        <Column
          field="disallowed_amount"
          title="Disallowed Amount"
          filterable={false}
          cell={(props: any) => (
            <td className="text-end">
              {formatIndianNumber(props.dataItem?.disallowed_amount || 0)}
            </td>
          )}
          width={180}
        />
        <Column
          field="additional_expense_amount"
          title="Additional Expense Amount"
          filterable={false}
          cell={(props: any) => (
            <td className="text-end">
              {formatIndianNumber(
                props.dataItem?.additional_expense_amount || 0
              )}
            </td>
          )}
          width={250}
        />
        <Column
          field="total_amount"
          title="Total Amount"
          filterable={false}
          cell={(props: any) => (
            <td className="text-end">
              {formatIndianNumber(props.dataItem?.total_amount || 0)}
            </td>
          )}
          width={180}
        />

        <Column
          width={300}
          filterable={false}
          field="remarks"
          title="Remarks"
          cell={(props: any) => (
            <td>
              <div
                className="remarks-content"
                dangerouslySetInnerHTML={{
                  __html:
                    expandedRow === props.dataItem?.id
                      ? props.dataItem?.remarks
                      : truncateRemarks(props.dataItem?.remarks),
                }}
              />
              {props.dataItem?.remarks.length > 100 && (
                <span
                  className="readmore-btn"
                  onClick={() =>
                    setExpandedRow(
                      expandedRow === props.dataItem?.id
                        ? null
                        : props.dataItem?.id
                    )
                  }
                >
                  {expandedRow === props.dataItem?.id
                    ? "Read Less"
                    : "Read More"}
                </span>
              )}
            </td>
          )}
        />

        {(isEditAccess || isDeleteAccess || isPrintAccess) && (
          <Column
            field="id"
            title="Actions"
            cell={MyEditCommandCell}
            width={"150px"}
            locked={true}
            filterable={false}
          />
        )}
      </KendoGrid>
    </>
  );
};

const ViewExpenseDetailsDialog: React.FC = () => {
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const loading = useAppSelector((state) => state.expensedetails.loading);
  const ExpenseDetail = useAppSelector(
    (state) => state.expensedetails.ExpenseDetail
  );
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {dialogName === "viewSRExpenseDetails" && !loading && (
        <>
          <AppDialog className="expense-approverd-view-dialog">
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                }}
              >
                <Typography.h5>Expense Details</Typography.h5>
                <RxCross2
                  onClick={() => {
                    dispatch(closeDialog());
                    dispatch(clearExpenseDetail());
                  }}
                  style={{ fontSize: "25px", fontWeight: "bolder" }}
                />
              </div>
              <div
                style={{
                  height: "calc(100% - 10%)",
                  overflowY: "scroll",
                  padding: "10px",
                  borderRadius: "4px",
                }}
              >
                <table
                  className="table table-bordered"
                  style={{ borderColor: "black" }}
                >
                  <tbody>
                    <tr>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>Expense Date</strong>
                      </td>
                      <td>
                        {moment(ExpenseDetail?.expense_date).format(
                          "DD/MM/YYYY"
                        )}
                      </td>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>Sales Representative</strong>
                      </td>
                      <td>{ExpenseDetail?.sr_name}</td>
                    </tr>
                    <tr>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>To City</strong>
                      </td>
                      <td>{ExpenseDetail?.to_city_name}</td>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>From City</strong>
                      </td>
                      <td>{ExpenseDetail?.from_city_name}</td>
                    </tr>
                    <tr>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>Mode Of Transport</strong>
                      </td>
                      <td>{ExpenseDetail?.mode_of_transport_name}</td>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>Petrol Allowance</strong>
                      </td>
                      <td className="text-end">
                        {formatIndianNumber(
                          ExpenseDetail?.petrol_rate_per_km || 0
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td rowSpan={2} style={{ backgroundColor: "lightgrey" }}>
                        <strong>Cities Visited</strong>
                      </td>
                      <td rowSpan={2}>
                        {ExpenseDetail?.city_town_visisted_name}
                      </td>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>Fare For Transport</strong>
                      </td>
                      <td className="text-end">
                        {formatIndianNumber(
                          ExpenseDetail?.fare_for_transport || 0
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>Sales Amount</strong>
                      </td>
                      <td className="text-end">
                        {formatIndianNumber(ExpenseDetail?.sales_amount || 0)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>Working agent/Area/Town:</strong>
                      </td>
                      <td colSpan={3}>{ExpenseDetail?.remarks}</td>
                    </tr>
                  </tbody>
                </table>

                <Typography.h5>Expenses</Typography.h5>
                <table
                  className="table table-bordered"
                  style={{ borderColor: "black" }}
                >
                  <tbody>
                    {ExpenseDetail.expense_details_list.map((item: any) => (
                      <tr>
                        <td style={{ backgroundColor: "lightgrey" }}>
                          <strong>{item?.expense_name?.toUpperCase()}</strong>
                        </td>
                        <td style={{ textAlign: "end" }}>
                          {formatIndianNumber(item.value || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Typography.h5>Expense Verification</Typography.h5>
                <table
                  className="table table-bordered"
                  style={{ borderColor: "black" }}
                >
                  <tbody>
                    <tr>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>Verified By</strong>
                      </td>
                      <td>{ExpenseDetail.verify_by_name}</td>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          Net Amount <span style={{ color: "green" }}>(A)</span>
                        </strong>{" "}
                      </td>
                      <td style={{ textAlign: "end" }}>
                        {formatIndianNumber(ExpenseDetail.net_amount || 0)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>Verification Date</strong>
                      </td>
                      <td>
                        {ExpenseDetail?.verification_date
                          ? moment(ExpenseDetail?.verification_date).format(
                              "DD/MM/YYYY"
                            )
                          : ""}
                      </td>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          Disallowed Amount{" "}
                          <span style={{ color: "red" }}>(B)</span>
                        </strong>
                      </td>
                      <td style={{ textAlign: "end" }}>
                        {formatIndianNumber(
                          ExpenseDetail.disallowed_amount || 0
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>Verification Status</strong>
                      </td>
                      <td
                        className={
                          ExpenseDetail?.verification_status
                            ? "text-success"
                            : "text-danger"
                        }
                      >
                        <strong>
                          {ExpenseDetail?.verification_status
                            ? "Verified"
                            : "Not Verified"}
                        </strong>
                      </td>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          Additional Amount{" "}
                          <span style={{ color: "green" }}> (C)</span>
                        </strong>
                      </td>
                      <td style={{ textAlign: "end" }}>
                        {formatIndianNumber(
                          ExpenseDetail.additional_expense_amount || 0
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong>Verification Remarks</strong>
                      </td>
                      <td>{ExpenseDetail?.verification_remarks}</td>
                      <td style={{ backgroundColor: "lightgrey" }}>
                        <strong
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          Total Amount
                          <p className="m-0">
                            (<span style={{ color: "green" }}>{"A"}</span> -{" "}
                            <span style={{ color: "red" }}>{"B"}</span> +{" "}
                            <span style={{ color: "green" }}>{"C"}</span>){" "}
                          </p>
                        </strong>
                      </td>
                      <td style={{ textAlign: "end" }}>
                        {formatIndianNumber(ExpenseDetail.total_amount || 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          </AppDialog>
        </>
      )}
    </>
  );
};

const CommonFilterCell = (props: any) => {
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterCustomExpenseApproved: any = useAppSelector(
    (state) => state.expenseApproved.FilterCustomExpenseApproved
  );
  const isFilterRef = useRef(true);
  const [inputValue, setInputValue] = useState(props.value || "");
  const [inputField, setInputField] = useState(props.field || "");

  const onChange = (event: any) => {
    setInputValue(event.target.value);
    setInputField(event.target.name);
  };

  const delayedApiCall = React.useCallback(
    _debounce((updatedFilters: any) => {
      dispatch(setCurrentPage(0));
      const payload = {
        ...updatedFilters,
        verification_status:updatedFilters?.verification_status?.value,
        limit: +pageLimit,
        skip: +currentPage * +pageLimit,
      };

      dispatch(getAllSRExpensesForVerify(payload));
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    if (!isFilterRef.current) {
      const updatedFilters = {
        ...FilterCustomExpenseApproved,
        [inputField]: inputValue,
      };

      dispatch(setFilterCustomExpenseApproved(updatedFilters));
      delayedApiCall(updatedFilters);
    } else {
      isFilterRef.current = false;
    }
    return () => {
      delayedApiCall.cancel();
    };
  }, [inputValue, delayedApiCall]);

  const onClearButtonClick = (event: any) => {
    event.preventDefault();
    props.onChange({
      value: null,
      operator: "",
      syntheticEvent: event,
    });

    const updatedFilters = {
      ...FilterCustomExpenseApproved,
      [props.field]: "",
    };

    dispatch(setFilterCustomExpenseApproved(updatedFilters));

    const payload = {
      ...updatedFilters,
      verification_status:updatedFilters?.verification_status?.value,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };

    dispatch(getAllSRExpensesForVerify(payload));
  };

  return (
    <div className="d-flex">
      <TextBox
        value={FilterCustomExpenseApproved[props.field] || ""}
        name={props.field || ""}
        onChange={onChange}
      />
      <Button
        svgIcon={filterClearIcon}
        title="Clear"
        disabled={!FilterCustomExpenseApproved[props.field]}
        onClick={onClearButtonClick}
        type="button"
        style={{
          marginLeft: "8px",
        }}
      ></Button>
    </div>
  );
};

const CommonCheckBoxFilterCell = (props: any) => {
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterCustomExpenseApproved: any = useAppSelector(
    (state) => state.expenseApproved.FilterCustomExpenseApproved
  );
  const isFilterRef = useRef(true);
  const [selectedValue, setSelectedValue] = useState<any>(
    FilterCustomExpenseApproved[props.field] || null
  );

  const delayedApiCall = React.useCallback(
    _debounce((updatedFilters: any) => {
      dispatch(setCurrentPage(0));
      const payload = {
        ...updatedFilters,
        verification_status: updatedFilters?.verification_status?.value,
        limit: +pageLimit,
        skip: +currentPage * +pageLimit,
      };
      dispatch(getAllSRExpensesForVerify(payload));
    }, 300),
    [dispatch, pageLimit, currentPage]
  );

  useEffect(() => {
    if (!isFilterRef.current) {
      const updatedFilters = {
        ...FilterCustomExpenseApproved,
        [props.field]: selectedValue,
      };

      dispatch(setFilterCustomExpenseApproved(updatedFilters));
      delayedApiCall(updatedFilters);
    } else {
      isFilterRef.current = false;
    }
    return () => {
      delayedApiCall.cancel();
    };
  }, [selectedValue]);

  const onChange = (event: ComboBoxChangeEvent) => {
    setSelectedValue(event.target.value);
  };

  // const onClearButtonClick = (event: any) => {
  //   event.preventDefault();
  //   setSelectedValue(null);
  //   const updatedFilters = {
  //     ...FilterCustomExpenseApproved,
  //     [props.field]: null,
  //   };

  //   dispatch(setFilterCustomExpenseApproved(updatedFilters));
  //   dispatch(
  //     getAllSRExpensesForVerify({
  //       ...updatedFilters,
  //       limit: +pageLimit,
  //       skip: +currentPage * +pageLimit,
  //     })
  //   );
  // };

  const FILTER_SELECTION = [
    { label: "Verified", value: true },
    { label: "Pending", value: false },
  ];

  return (
    <div className="d-flex">
      <ComboBox
        data={FILTER_SELECTION}
        value={selectedValue}
        textField="label"
        dataItemKey="value"
        onChange={onChange}
        placeholder="Select Status"
      />
      {/* <Button
        svgIcon={filterClearIcon}
        title="Clear"
        disabled={selectedValue === null}
        onClick={onClearButtonClick}
        type="button"
        style={{ marginLeft: "8px" }}
      ></Button> */}
    </div>
  );
};

export default ExpenseApproved;
